import React, { useRef, useState, useEffect } from 'react';
import { FiChevronDown, FiChevronUp, FiUsers } from 'react-icons/fi';
import {
  ReplyAttachments,
  ReplyContainer,
  ReplyContent,
  ReplyHeader,
  ReplyMeta,
  ReplyRecipientItem,
  ReplyRecipientsList,
  ToggleExpand
} from './styled';
import { useSmartScroll } from 'hooks/useSmartScroll';

/**
 * @typedef {Object} ParentComment
 * @property {string} id - Unique identifier for the comment
 * @property {string} user_id - ID of the user who created the comment
 * @property {string} description - HTML content of the comment
 * @property {string} creation_date - ISO date string of when the comment was created
 * @property {Attachment[]} [attachments] - List of attachments associated with the comment
 */

/**
 * Component that displays a referenced parent comment with expand/collapse functionality
 * and the ability to scroll to the original comment.
 * @param {Object} props - Component props
 * @param {ParentComment|null} props.parentComment - The parent comment data to display
 * @returns {React.ReactElement|null} The rendered component or null if no parent comment
 */
export function RepliedCommentBox({
  parentComment,
  scrollOffset = 80,
  hightlightTarget = true
}) {
  /**
   * Reference to the content element to measure its dimensions
   * @type {React.RefObject<HTMLDivElement>}
   */
  const contentRef = useRef(null);

  /**
   * State to track content overflow and expansion
   * @type {[null|boolean, React.Dispatch<React.SetStateAction<null|boolean>>]}
   * - null: Content does not overflow (no toggle needed)
   * - false: Content overflows and is collapsed
   * - true: Content overflows and is expanded
   */
  const [contentState, setContentState] = useState(null);

  // Use custom scroll hook
  const scrollToElement = useSmartScroll({
    offset: scrollOffset,
    Highlight: hightlightTarget,
    highlightColor: '#d7dbe2'
  });

  /**
   * Effect to detect if content overflows its container when component mounts or parent comment changes
   */
  useEffect(() => {
    if (parentComment && contentRef.current) {
      const element = contentRef.current;
      setContentState(
        element.scrollHeight > element.clientHeight ? false : null
      );
    }
  }, [parentComment, contentRef]);

  if (!parentComment) return null;

  const {
    id,
    user_id: userId,
    description,
    creation_date: createdAt,
    attachments
  } = parentComment;

  /**
   * Scrolls the page to the original parent comment
   */
  const handleScroll = () => {
    scrollToElement(id);
  };

  /**
   * Toggles between expanded and collapsed states
   * @param {React.MouseEvent} e - The click event
   */
  const toggleExpand = (e) => {
    e.stopPropagation();
    setContentState((state) => (state === false ? true : false));
  };

  /**
   * Derived value that indicates if content is overflowing
   * @type {boolean}
   */
  const isOverflowing = contentState !== null;

  /**
   * Derived value that indicates if content is expanded
   * @type {boolean}
   */
  const isExpanded = contentState === true;

  return (
    <ReplyContainer>
      <ReplyHeader>Em resposta a:</ReplyHeader>
      <ReplyMeta>
        <span>{userId} - </span>
        {new Date(createdAt).toLocaleDateString('pt-BR', {
          day: '2-digit',
          month: 'long',
          year: 'numeric'
        })}
      </ReplyMeta>

      {parentComment?.to && parentComment.to.length > 0 && (
        <div style={{ marginBottom: '8px' }}>
          <strong
            style={{
              fontSize: '0.8rem',
              color: '#525f7f',
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <FiUsers style={{ marginRight: '4px' }} />
            Destinatários ({parentComment.to.length}):
          </strong>
          <ReplyRecipientsList>
            {parentComment.to.map((recipient, idx) => (
              <ReplyRecipientItem key={idx}>{recipient}</ReplyRecipientItem>
            ))}
          </ReplyRecipientsList>
        </div>
      )}

      <ReplyContent
        ref={contentRef}
        onClick={handleScroll}
        className={isExpanded ? 'expanded' : ''}
        dangerouslySetInnerHTML={{ __html: description }}
      />
      {isOverflowing && (
        <ToggleExpand onClick={toggleExpand}>
          {isExpanded ? (
            <span>
              <FiChevronUp
                style={{ verticalAlign: 'middle', marginRight: '4px' }}
              />
              Ver menos
            </span>
          ) : (
            <span>
              <FiChevronDown
                style={{ verticalAlign: 'middle', marginRight: '4px' }}
              />
              Ver mais
            </span>
          )}
        </ToggleExpand>
      )}
      {attachments?.length > 0 && (
        <ReplyAttachments>
          {attachments.length} anexo
          {attachments.length > 1 ? 's' : ''}
        </ReplyAttachments>
      )}
    </ReplyContainer>
  );
}
